import api from "./api";

const UPLOAD_TIMEOUT_MS = 120_000;

const licencasService = {
  criarSolicitacao: async (solicitacaoRequest) => {
    try {
      const response = await api.post(
        `SolicitacaoDeLicenca`,
        solicitacaoRequest,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          timeout: UPLOAD_TIMEOUT_MS,
        },
      );

      return response.data;
    } catch (error) {
      throw error.response?.data || { message: "Erro ao criar solicitação" };
    }
  },

  criarDocumento: async (solicitacaoDeLicencaId, arquivo, descricao) => {
    try {
      const formData = new FormData();
      formData.append("solicitacaoDeLicencaId", solicitacaoDeLicencaId);

      const nomeArquivo = `${descricao}.${arquivo.name.split(".").pop()}`;
      const arquivoRenomeado = new File([arquivo], nomeArquivo, {
        type: arquivo.type,
      });

      formData.append("arquivo", arquivoRenomeado);

      const response = await api.post(`Documento`, formData);

      return response.data;
    } catch (error) {
      throw error.response?.data || { message: "Erro ao enviar documento" };
    }
  },

  listarMinhasSolicitacoes: async () => {
    try {
      const response = await api.get(`SolicitacaoDeLicenca/Usuario/Minhas`);

      return response.data.data || [];
    } catch (error) {
      throw error.response?.data || { detail: "Erro ao listar solicitações" };
    }
  },

  listarMinhasSolicitacoesPaginado: async ({
    tipoLicencaId,
    empreendimentoId,
    dataInicio,
    dataFim,
    pageNumber = 1,
    pageSize = 10,
  } = {}) => {
    try {
      const params = Object.fromEntries(
        Object.entries({
          tipoLicencaId,
          empreendimentoId,
          dataInicio,
          dataFim,
          pageNumber,
          pageSize,
        }).filter(([_, value]) => value !== "" && value != null),
      );

      const response = await api.get(`SolicitacaoDeLicenca/Usuario/Minhas`, {
        params,
      });

      const items = response.data?.data || [];
      const pagination = response.data?.pagination || {};

      return {
        items,
        totalCount: pagination.totalCount ?? items.length,
        pageNumber: pagination.pageNumber ?? pageNumber,
        pageSize: pagination.pageSize ?? pageSize,
        totalPages:
          pagination.totalPages ??
          Math.ceil((pagination.totalCount ?? items.length) / pageSize),
      };
    } catch (error) {
      throw error.response?.data || { detail: "Erro ao listar solicitações" };
    }
  },

  listarSolicitacoesAnalista: async () => {
    try {
      const response = await api.get(`SolicitacaoDeLicenca/Analista/Todas`);
      return response.data.data || [];
    } catch (error) {
      throw error.response?.data || { detail: "Erro ao listar solicitações" };
    }
  },

  obterSolicitacao: async (id) => {
    try {
      const response = await api.get(`SolicitacaoDeLicenca/${id}/`);

      return response.data?.data ?? response.data;
    } catch (error) {
      console.error("Erro ao obter solicitação:", error);
      const payload = error.response?.data || {
        detail: "Erro ao obter solicitação",
      };
      if (error.response?.status) {
        payload.status = error.response.status;
      }
      throw payload;
    }
  },

  atualizarSolicitacao: async (id, formData) => {
    try {
      const response = await api.patch(`licencas/${id}/`, formData, {
        timeout: UPLOAD_TIMEOUT_MS,
      });

      return response.data;
    } catch (error) {
      throw error.response?.data || { detail: "Erro ao atualizar solicitação" };
    }
  },

  atualizarSolicitacaoAnalista: async (
    id,
    status,
    observacoes,
    rowVersion,
    opcoes = {},
  ) => {
    try {
      const formData = new FormData();
      formData.append("id", id);
      formData.append("status", status);
      formData.append("observacoes", observacoes || "");
      if (rowVersion !== undefined && rowVersion !== null) {
        formData.append("rowVersion", rowVersion);
      }
      if (opcoes.dataValidade) {
        formData.append("dataValidade", opcoes.dataValidade);
      }
      if (opcoes.possuiRama !== undefined && opcoes.possuiRama !== null) {
        formData.append("possuiRama", opcoes.possuiRama);
      }
      if (opcoes.intervaloRamaMeses !== undefined && opcoes.intervaloRamaMeses !== null) {
        formData.append("intervaloRamaMeses", opcoes.intervaloRamaMeses);
      }

      const response = await api.put(`SolicitacaoDeLicenca`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      return response.data;
    } catch (error) {
      throw (
        error.response?.data || { message: "Erro ao atualizar solicitação" }
      );
    }
  },

  previewRama: async (solicitacaoId, dataValidade, intervaloRamaMeses, dataReferencia = null) => {
    try {
      const response = await api.post(
        `SolicitacaoDeLicenca/${solicitacaoId}/rama/preview`,
        {
          dataValidade,
          intervaloRamaMeses,
          dataReferencia,
        },
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: "Erro ao calcular preview do RAMA" };
    }
  },

  obterDocumentosSolicitacao: async (idDaSolicitacao) => {
    try {
      const response = await api.get(
        `/documento/solicitacao/${idDaSolicitacao}`,
      );

      if (response.data && response.data.data) {
        return response.data.data;
      }

      return [];
    } catch (error) {
      return [];
    }
  },

  emitirLicenca: async (id) => {
    try {
      const response = await api.post(`SolicitacaoDeLicenca/${id}/emitir`);

      return response.data;
    } catch (error) {
      throw error.response?.data || { message: "Erro ao emitir licença" };
    }
  },

  listarSolicitacoesSecretario: async () => {
    try {
      const response = await api.get(`SolicitacaoDeLicenca/Secretario/Todas`);
      return response.data.data || [];
    } catch (error) {
      throw (
        error.response?.data || { detail: "Erro ao listar solicitações" }
      );
    }
  },

  atualizarSolicitacaoSecretario: async (id, status, observacoes, rowVersion) => {
    try {
      const formData = new FormData();
      formData.append("id", id);
      formData.append("status", status);
      formData.append("observacoes", observacoes || "");
      if (rowVersion !== undefined && rowVersion !== null) {
        formData.append("rowVersion", rowVersion);
      }

      const response = await api.put(
        `SolicitacaoDeLicenca/Secretario`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      return response.data;
    } catch (error) {
      throw (
        error.response?.data || { message: "Erro ao atualizar solicitação" }
      );
    }
  },
};

export default licencasService;
